import React, { Suspense, useEffect, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { CaretDown, CaretRight, CheckCircle } from '@phosphor-icons/react';
import { formatSrpSalaryDisplay } from '../../../../../utils/data/format-job-card-labels';
import { convertSalaryToMonthlyEUR } from '../utils/salaryConversion';
import { fixMojibake } from '../../../../../utils/text/fix-mojibake';
import { normalizeLiteralEscapeSequences } from '../../../../../utils/text/normalize-literal-escape-sequences';
import { useJdpSectionExpanded } from '../../../../../hooks/useJdpSectionExpanded';
import { adjustBrandColor } from '../../../../../utils/brand-color/adjust-brand-color';
import { shouldLinkCompanyProfile } from '../../../../../utils/company/should-link-company-profile';
import { companyProfileUrlPath } from '../../../../../utils/routing/company-profile-url-path';
import { resolveSearchHitIsTyomarkkinatori } from '../../../../../utils/search/resolve-search-hit-is-tyomarkkinatori';
import { buildFullCompanyAddress, formatPostalAddressLine } from '../../../../../utils/geocoding/match-job-location-with-company-address';
import { TYOMARKKINATORI_PURPLE } from '../utils/constants';
import { OverviewSectionHeader, JobDetailsSection, SalaryDistributionChart, MatchScoreBreakdown, ApplyButton, CompanyLogo } from '../components';
import { HubCrossLinks, type HubCrossLink } from '../../../../../components/hub/HubCrossLinks';
import { resolveSearchHitHostedLogoUrl } from '../../../../../utils/search/resolve-search-hit-company-display';
import { RoleFollowButton } from '../../../../../components/roles/RoleFollowButton';
import { SimilarJobsSection } from '../components/SimilarJobsSection';
import { BrandRail } from './BrandRail';
import { MetaGrid } from './MetaGrid';
import { CompanyCard } from './CompanyCard';
import { CollectiveAgreementNotice } from './CollectiveAgreementNotice';
import type { OverviewSectionHeaderProps } from '../components/OverviewSectionHeader';
import type { DisplayJob } from '../../../../../types';
import { buildJobMapCommuteHref } from '../../../../../utils/map/job-map-commute-href';
import type { Locale } from '../../../../../utils/routing/generate-url';
import { Msg, useFormatMessage } from '../../../../../i18n-review/client/Msg';
import { findRoleForNormalisedTitle } from '../../../../../utils/role/find-role-for-title';
import { buildRoleHubPath } from '../../../../../utils/role/role-hub-config';
import { buildLocationSrpPath } from '../../../../../utils/location/build-location-srp-path';
import { findFinlandHubSlugByCityName } from '../../../../../utils/location/finland-hub-config';
import { getFinnishRoleGenitive } from '../../../../../utils/i18n/finnish-role-genitive';
import { getCityInessiveOrNull } from '../../../../../utils/seo/generate-srp-seo-meta';

// Lazy-loaded because it pulls in the entire mapbox-gl dependency (~700kB JS + 5kB CSS
// asset). The user must open the "Location" pane to mount the map, so lazy-loading is safe.
const CompanyLocationMap = React.lazy(() =>
  import('../../../../../components/CompanyLocationMap').then((m) => ({ default: m.CompanyLocationMap })),
);

export interface JobDetailPageV2Props {
  displayJob: DisplayJob;
  title?: string;
  normalisedTitle?: string;
  companyId?: string | null;
  location?: string;
  remoteness?: string;
  field?: string;
  jobType?: string;
  normalisedRoleLevel?: string;
  requiredLanguages?: string[];
  peopleManagementTier?: string;
  educationLevel?: string;
  minimumYearsOfExperience?: number | null;
  collectiveAgreementId?: string | null;
  collectiveAgreementName?: string | null;
  collectiveAgreementMatchType?: 'explicit' | 'inferred' | 'none' | null;
  collectiveAgreementReasoning?: string | null;
  jobLocale: string;
  displayDescription?: string;
  overviewHeaderProps: OverviewSectionHeaderProps;
  applyUrl: string;
  isApplied: boolean;
  setAppliedJobState: (job: any, applied: boolean) => Promise<void>;
  /** When provided, the brand rail's apply button renders a secondary "Tailor my CV" action. */
  onTailorCv?: () => void;
  formattedPostedDate: string | null;
  postedDaysAgo: number | null;
  formattedLastSeenDate: string | null;
  formattedApplicationDate: string | null;
  formattedEstimatedExpiryDate: string | null;
  matchScoreData: import('../../../../../utils/data/calculate-job-match-score').MatchScoreBreakdown | null;
  salaryRangeMin?: number;
  salaryRangeMax?: number;
  locationForChart?: string[];
  currency?: string;
  salaryPeriod?: 'annual' | 'monthly' | 'daily' | 'hourly';
  salaryListingProvenance?: import('../../../../../utils/job-detail/resolve-job-salary-listing-provenance').JobSalaryListingProvenance;
  optionalLanguages?: string[];
  perks?: string[];
  jobDetailsRef: React.RefObject<HTMLDivElement>;
  currentLocaleTrends?: {
    roleDistribution: Record<string, number>;
    roleLevelDistribution?: Record<string, number>;
    locationDistribution?: Record<string, number>;
  };
  relatedPages: HubCrossLink[];
  effectiveSimilarJobs: unknown[];
  similarJobsSeeAllHref?: string;
}

/**
 * V2 (brand color panel) redesign of the job detail page — replaces just the core JobPosting
 * card (breadcrumb/language-switcher/footer/engagement-strip/tailor-cv-modal stay shared,
 * rendered by +Page.tsx around this component regardless of variant). Reuses OverviewSectionHeader,
 * JobDetailsSection, SalaryDistributionChart, MatchScoreBreakdown, SimilarJobsSection, and
 * InternalLinksSection as-is (all fully prop-driven, already tested, doing complex interactive
 * work that would be wasteful and risky to duplicate) — only the brand rail, meta grid, and
 * company card (most-hired-roles bars + role-level mix, replacing the tabbed donut charts) are
 * new, matching the design handoff's structural identity.
 */
export function JobDetailPageV2(props: JobDetailPageV2Props): React.ReactElement {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const {
    displayJob,
    title,
    normalisedTitle,
    companyId,
    location,
    remoteness,
    field,
    jobType,
    normalisedRoleLevel,
    requiredLanguages,
    peopleManagementTier,
    educationLevel,
    minimumYearsOfExperience,
    collectiveAgreementId,
    collectiveAgreementName,
    collectiveAgreementMatchType,
    collectiveAgreementReasoning,
    jobLocale,
    displayDescription,
    overviewHeaderProps,
    applyUrl,
    isApplied,
    setAppliedJobState,
    onTailorCv,
    formattedPostedDate,
    postedDaysAgo,
    formattedLastSeenDate,
    formattedApplicationDate,
    formattedEstimatedExpiryDate,
    matchScoreData,
    salaryRangeMin,
    salaryRangeMax,
    locationForChart,
    currency,
    salaryPeriod,
    salaryListingProvenance,
    optionalLanguages,
    perks,
    jobDetailsRef,
    currentLocaleTrends,
    relatedPages,
    effectiveSimilarJobs,
    similarJobsSeeAllHref,
  } = props;

  const isTyomarkkinatori = resolveSearchHitIsTyomarkkinatori(displayJob as unknown as Record<string, unknown>);
  // Työmarkkinatori listings always brand purple, same override the SRP card and V1 detail
  // page apply — without it this panel falls back to the neutral color since TMT companies'
  // raw brandColour (often black/unset) reads as achromatic to adjustBrandColor.
  const adjustedBrand = adjustBrandColor(isTyomarkkinatori ? TYOMARKKINATORI_PURPLE : displayJob.brandColour);
  const companyDisplayName = displayJob.companyName ?? companyId ?? '';
  const canLinkCompanyProfile = shouldLinkCompanyProfile({ companyId: companyId ?? '', isTyomarkkinatori });
  const companyProfileHref = canLinkCompanyProfile && companyId ? companyProfileUrlPath(jobLocale as 'en' | 'fi' | 'sv', companyId) : '';
  // Role hub links: "More {role} jobs" and, when the job's city is a curated hub, the role x city
  // page. Job pages carry most of the site's search impressions, so this is the main path from
  // a listing to its role hub (utils/role/find-role-for-title.ts matches by exact normalisedTitle).
  const roleHubLinks = ((): Array<{ href: string; label: string }> => {
    const loc = jobLocale as 'en' | 'fi' | 'sv';
    const role = findRoleForNormalisedTitle(normalisedTitle, loc);
    if (!role) return [];
    const display = role.displayNames[loc];
    const genitive = loc === 'fi' ? getFinnishRoleGenitive(display) : display;
    const roleLower = loc === 'fi' && genitive !== display ? genitive.toLowerCase() : display;
    const links: Array<{ href: string; label: string }> = [
      {
        href: buildRoleHubPath(role.slug, loc),
        label: formatMessage(
          { id: 'jobDetail.roleHub.moreJobs', defaultMessage: 'More {role} jobs', description: 'Job page link to the role hub; {role} is the role name (Finnish: genitive, lowercase)' },
          { role: roleLower },
        ),
      },
    ];
    const cityRaw = Array.isArray(displayJob.location) ? displayJob.location[0] : undefined;
    const citySlug = findFinlandHubSlugByCityName(cityRaw);
    if (citySlug) {
      const cityName = String(cityRaw).split(',')[0].trim();
      const cityLabel = loc === 'fi' ? (getCityInessiveOrNull(cityName) ?? cityName) : cityName;
      links.push({
        href: `/${loc}${buildLocationSrpPath(citySlug, loc, undefined, role.slug)}`,
        label: formatMessage(
          { id: 'jobDetail.roleHub.jobsInCity', defaultMessage: '{role} jobs in {city}', description: 'Job page link to the role x city page; {role} is the role name (Finnish: genitive), {city} the city (Finnish: inessive)' },
          { role: genitive, city: cityLabel },
        ),
      });
    }
    return links;
  })();
  const companyLinkTitle = formatMessage(
    { id: 'hit.companyLinkTitle', defaultMessage: 'Open company profile — data and insights for {company}', description: 'Tooltip for the employer logo and name link on a job card' },
    { company: companyDisplayName },
  );
  const insightsCueLabel = formatMessage({
    id: 'hit.companyInsights.cue',
    defaultMessage: 'Company insights',
    description: 'Short label under the employer name on a job card; the block links to the company profile',
  });
  const salaryStats = displayJob.companySalaryStats || (displayJob as unknown as { salaryStats?: unknown }).salaryStats;
  const [sectionExpanded, toggleSectionExpanded] = useJdpSectionExpanded();
  // Defaults to the full description (not the tldr) so the SSR HTML that search-engine/Google
  // Jobs indexers read always carries the complete job text, not text hidden behind a click.
  // "Show summary" collapses to the AI tldr; resets per job (not persisted like
  // useJdpSectionExpanded's sections) since it toggles content that's different on every job,
  // not a standing per-user layout preference.
  const [descriptionExpanded, setDescriptionExpanded] = useState(true);
  useEffect(() => {
    setDescriptionExpanded(true);
  }, [displayJob.objectID]);

  // Mobile crossfade mini-header (matches the native app's job screen): fades in once the
  // rail's own identity block has scrolled out of view, fades out again if the user scrolls
  // back up. Desktop doesn't use this at all, the whole rail is sticky instead (BrandRail.tsx).
  const [miniHeaderVisible, setMiniHeaderVisible] = useState(false);
  useEffect(() => {
    const sentinel = document.getElementById('brand-rail-mobile-sentinel');
    if (!sentinel || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setMiniHeaderVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [displayJob.objectID]);

  // The mini-header sits below the main site nav (also position:sticky, top:0), not on top of
  // it, so it needs that nav's rendered height as its own `top` offset. Measured at runtime
  // instead of hardcoded since the nav's height can change (locale text length, breakpoints).
  const [siteNavHeight, setSiteNavHeight] = useState(0);
  useEffect(() => {
    const measure = () => {
      const header = document.querySelector('header');
      setSiteNavHeight(header ? header.getBoundingClientRect().height : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Real, employer-quoted pay (not extracted-without-quote, not modelled/estimated) — shown
  // prominently above the collapsible salary comparison section per its own request, since that
  // section can be collapsed and this is the one number worth surfacing regardless.
  const isRealSalary = salaryListingProvenance === 'employer_quote';
  const hasRawSalary =
    isRealSalary &&
    typeof salaryRangeMin === 'number' &&
    typeof salaryRangeMax === 'number' &&
    salaryRangeMin > 0 &&
    salaryRangeMax > 0;
  const convertedRealSalary = hasRawSalary
    ? convertSalaryToMonthlyEUR(salaryRangeMin!, salaryRangeMax!, currency, salaryPeriod)
    : null;
  const realSalaryLabel = convertedRealSalary
    ? formatSrpSalaryDisplay({
        salaryRangeMin: convertedRealSalary.min,
        salaryRangeMax: convertedRealSalary.max,
        salaryIsEstimate: false,
        locale: intl.locale,
      })
    : null;

  const companyAddressLine = formatPostalAddressLine(
    buildFullCompanyAddress({
      registryStreet: displayJob.registryStreet,
      registryBuildingNumber: displayJob.registryBuildingNumber,
      registryPostCode: displayJob.registryPostCode,
      registryCity: displayJob.registryCity,
      address_fi: displayJob.address_fi,
      address_sv: displayJob.address_sv,
    }),
  );
  const hasRealCompanyAddress = Boolean(displayJob.companyGeocoordinates && companyAddressLine);

  return (
    <>
    <main id="job-main-content" className="mt-6">
      <div
        className="flex flex-col lg:flex-row items-stretch rounded-[24px] bg-white dark:bg-[#1F1509] shadow-[0_1px_3px_rgba(22,21,15,0.08)]"
      >
        <BrandRail
          displayJob={displayJob}
          panelColor={adjustedBrand.panel}
          panelOn={adjustedBrand.on}
          accentOnWhite={adjustedBrand.accentOnWhite}
          applyUrl={applyUrl}
          isApplied={isApplied}
          setAppliedJobState={setAppliedJobState}
          onTailorCv={onTailorCv}
          realSalaryLabel={realSalaryLabel}
          formattedPostedDate={formattedPostedDate}
          postedDaysAgo={postedDaysAgo}
          formattedLastSeenDate={formattedLastSeenDate}
          formattedApplicationDate={formattedApplicationDate}
          formattedEstimatedExpiryDate={formattedEstimatedExpiryDate}
          companyId={companyId}
          companyName={companyDisplayName}
          isTyomarkkinatori={isTyomarkkinatori}
          location={location}
          companyProfileHref={canLinkCompanyProfile ? companyProfileHref : undefined}
          companyLinkTitle={companyLinkTitle}
          insightsCueLabel={insightsCueLabel}
          locale={jobLocale}
        />

        <div className="flex-1 min-w-0 flex flex-col gap-5 p-6 md:p-8">
          {/* Header row: company link, H1 title/subtitle, match ring + bookmark + overflow menu.
              Stacked on mobile — side by side, the action-icon cluster's fixed width otherwise
              squeezes the title column narrow enough to force short titles onto two lines. */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div className="min-w-0">
              {/* Company name + insights link live on the brand rail now (BrandRail.tsx) — showing
                  them here too would just duplicate it right next to the logo/rail. */}
              <h1 className="m-0 [font-family:'Space_Grotesk',sans-serif] text-[26px] md:text-[30px] font-bold leading-tight text-[#16150F] dark:text-[#F3EFE4]">
                {fixMojibake(title || normalisedTitle || '')}
              </h1>
              {normalisedTitle && normalisedTitle !== title ? (
                <div className="mt-1 text-sm text-[#6E6659] dark:text-[#B0A896]">{fixMojibake(normalisedTitle)}</div>
              ) : null}
              {normalisedTitle && !isTyomarkkinatori ? (
                <div className="mt-2">
                  <RoleFollowButton roleTitle={normalisedTitle} surface="job-page" />
                </div>
              ) : null}
              {roleHubLinks.length > 0 ? (
                <nav aria-label="Role hubs" className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  {roleHubLinks.map((link) => (
                    <a key={link.href} href={link.href} className="text-[#6E4F00] dark:text-[#E9C46A] underline-offset-2 hover:underline">
                      {link.label}
                    </a>
                  ))}
                </nav>
              ) : null}
            </div>
            <div className="flex flex-none items-center">
              <OverviewSectionHeader
                {...overviewHeaderProps}
                ringColorOverride={adjustedBrand.panel}
                labelColorOverride={{ light: '#16150F', dark: '#F3EFE4' }}
              />
            </div>
          </div>

          <MetaGrid
            location={location}
            mapCommuteHref={buildJobMapCommuteHref(intl.locale as Locale, displayJob._geoloc)}
            remoteness={remoteness}
            field={field}
            jobType={jobType}
            normalisedRoleLevel={normalisedRoleLevel}
            requiredLanguages={requiredLanguages}
            peopleManagementTier={peopleManagementTier}
            educationLevel={educationLevel}
            minimumYearsOfExperience={minimumYearsOfExperience}
          />

          <CollectiveAgreementNotice
            collectiveAgreementId={collectiveAgreementId}
            collectiveAgreementName={collectiveAgreementName}
            collectiveAgreementMatchType={collectiveAgreementMatchType}
            collectiveAgreementReasoning={collectiveAgreementReasoning}
            jobLocale={jobLocale}
          />

          {salaryStats || displayJob.locationSalaryStats || displayJob.allSalaryStats ? (
            <div className="border-t border-[rgba(22,21,15,0.08)] dark:border-[#4A3520]/70 pt-5">
              {realSalaryLabel ? (
                <div className="mb-3 flex items-center gap-2 rounded-xl border border-jc-accent/30 bg-jc-accent-muted px-3 py-2.5">
                  <CheckCircle weight="fill" className="h-5 w-5 shrink-0 text-jc-accent" aria-hidden />
                  <p className="m-0 text-[13px] leading-snug">
                    <span className="font-bold text-jc-accent [font-family:'Space_Grotesk',sans-serif] text-[15px]">{realSalaryLabel}</span>{' '}
                    <span className="font-semibold text-jc-accent/90">
                      <Msg
                        id="jobDetailPageV2.realSalaryNote"
                        defaultMessage="— real pay, quoted by the employer"
                        description="Note next to the prominent real-salary figure shown above the collapsible salary comparison section, clarifying it's from the employer and not an estimate"
                      />
                    </span>
                  </p>
                </div>
              ) : null}
              <div className="mb-1 flex items-center gap-2">
                <div className="[font-family:'Space_Grotesk',sans-serif] text-sm font-bold text-[#16150F] dark:text-[#F3EFE4]">
                  <Msg id="jobDetailPageV2.salaryCompares" defaultMessage="How this salary compares" description="Heading for the salary benchmark section on the redesigned job detail page" />
                </div>
                <button
                  type="button"
                  onClick={() => toggleSectionExpanded('salaryContext')}
                  className="text-[#8C8474] hover:brightness-90 transition-transform cursor-pointer p-0.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
                  aria-label={sectionExpanded.salaryContext
                    ? formatMessage({ id: 'jobDetailPageV2.salaryCompares.collapse', defaultMessage: 'Collapse salary comparison', description: 'Button label to collapse/hide the salary comparison section' })
                    : formatMessage({ id: 'jobDetailPageV2.salaryCompares.expand', defaultMessage: 'Expand salary comparison', description: 'Button label to expand/show the salary comparison section' })}
                >
                  {sectionExpanded.salaryContext ? <CaretDown className="h-4 w-4" /> : <CaretRight className="h-4 w-4" />}
                </button>
              </div>
              {sectionExpanded.salaryContext && (
                <SalaryDistributionChart
                  salaryStats={salaryStats as never}
                  locationSalaryStats={displayJob.locationSalaryStats}
                  allSalaryStats={displayJob.allSalaryStats}
                  jobSalaryMin={salaryRangeMin}
                  jobSalaryMax={salaryRangeMax}
                  currency={currency}
                  salaryPeriod={salaryPeriod}
                  locale={jobLocale}
                  jobLocation={locationForChart}
                  jobTitle={normalisedTitle || title}
                  jobRoleLevel={normalisedRoleLevel}
                  jobSalaryListingProvenance={salaryListingProvenance}
                />
              )}
            </div>
          ) : null}

          {displayJob.tldr || displayDescription ? (
            <div className="border-t border-[rgba(22,21,15,0.08)] dark:border-[#4A3520]/70 pt-5">
              <p className="m-0 mb-2.5 text-[11.5px] italic leading-snug text-[#8C8474] dark:text-[#9C9384]">
                <Msg
                  id="jobDetailPageV2.descriptionDisclaimer"
                  defaultMessage="The text below is the employer's original job description, extracted as written. Other details on this page, like salary, responsibilities, and requirements, are interpreted from that text by our system, not values the employer explicitly confirmed, so treat them as our best interpretation rather than verified facts."
                  description="Small disclaimer shown above the job description, distinguishing the verbatim original description from structured fields elsewhere on the page (salary, responsibilities, etc.) that are LLM-interpreted from that same text, not directly confirmed by the employer"
                />
              </p>
              <div className="mb-2.5 [font-family:'Space_Grotesk',sans-serif] text-sm font-bold text-[#16150F] dark:text-[#F3EFE4]">
                {displayJob.tldr && !descriptionExpanded ? (
                  <Msg id="jobDetailPageV2.tldr" defaultMessage="At a glance" description="Heading for the AI-generated TL;DR summary section on the redesigned job detail page" />
                ) : (
                  <Msg id="jobDetailPageV2.aboutRole" defaultMessage="About the role" description="Heading for the job description section on the redesigned job detail page" />
                )}
              </div>
              <p className="m-0 text-[13.5px] leading-relaxed text-[#4A4438] dark:text-[#C4A882] whitespace-pre-line">
                {displayJob.tldr && !descriptionExpanded
                  ? fixMojibake(normalizeLiteralEscapeSequences(displayJob.tldr))
                  : fixMojibake(displayDescription || displayJob.tldr || '')}
              </p>
              {displayJob.tldr && displayDescription ? (
                <button
                  type="button"
                  onClick={() => setDescriptionExpanded((v) => !v)}
                  className="mt-2 text-[13px] font-semibold text-[#4A4438] underline-offset-2 hover:underline dark:text-[#C4A882]"
                >
                  {descriptionExpanded ? (
                    <Msg id="jobDetailPageV2.showSummary" defaultMessage="Show summary" description="Button that collapses the full job description back to the AI TL;DR summary" />
                  ) : (
                    <Msg id="jobDetailPageV2.readFullDescription" defaultMessage="Read full description" description="Button that expands the AI TL;DR summary into the full job description" />
                  )}
                </button>
              ) : null}
            </div>
          ) : null}

          {matchScoreData ? (
            <MatchScoreBreakdown
              matchScore={matchScoreData}
              expanded={sectionExpanded.matchBreakdown}
              onToggleExpanded={() => toggleSectionExpanded('matchBreakdown')}
            />
          ) : null}

          <JobDetailsSection displayJob={displayJob} optionalLanguages={optionalLanguages} perks={perks} jobDetailsRef={jobDetailsRef} />

          <CompanyCard displayJob={displayJob} companyId={companyId} panelColor={adjustedBrand.panel} jobLocale={jobLocale} currentLocaleTrends={currentLocaleTrends} />

          {hasRealCompanyAddress ? (
            <div className="border-t border-[rgba(22,21,15,0.08)] dark:border-[#4A3520]/70 pt-5">
              <div className="mb-1 flex items-center gap-2">
                <div className="[font-family:'Space_Grotesk',sans-serif] text-sm font-bold text-[#16150F] dark:text-[#F3EFE4]">
                  <Msg id="jobDetailPageV2.location" defaultMessage="Location" description="Heading for the company location map section on the redesigned job detail page" />
                </div>
                <button
                  type="button"
                  onClick={() => toggleSectionExpanded('location')}
                  className="text-[#8C8474] hover:brightness-90 transition-transform cursor-pointer p-0.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
                  aria-label={sectionExpanded.location
                    ? formatMessage({ id: 'jobDetailPageV2.location.collapse', defaultMessage: 'Collapse location map', description: 'Button label to collapse/hide the company location map' })
                    : formatMessage({ id: 'jobDetailPageV2.location.expand', defaultMessage: 'Expand location map', description: 'Button label to expand/show the company location map' })}
                >
                  {sectionExpanded.location ? <CaretDown className="h-4 w-4" /> : <CaretRight className="h-4 w-4" />}
                </button>
              </div>
              {sectionExpanded.location && (
                <Suspense fallback={<div className="w-full h-[400px] bg-jc-page/50 rounded-xl animate-pulse" aria-hidden="true" />}>
                  <CompanyLocationMap
                    locations={[{
                      lat: displayJob.companyGeocoordinates!.lat,
                      lng: displayJob.companyGeocoordinates!.lng,
                      address: companyAddressLine,
                      isPrimary: true,
                    }]}
                    companyName={companyDisplayName}
                    companyId={companyId ?? undefined}
                  />
                </Suspense>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-6">
        <SimilarJobsSection similarJobs={effectiveSimilarJobs as never} seeAllHref={similarJobsSeeAllHref} />
      </div>
      <div className="mt-6">
        <HubCrossLinks
          id="related-pages"
          variant="cards"
          locale={jobLocale as 'en' | 'fi' | 'sv'}
          heading={formatMessage({ id: 'jobDetail.relatedPages.title', defaultMessage: 'Explore more', description: 'Heading of the job page block linking to the role, city, employer and salary hub pages' })}
          intro={formatMessage({ id: 'jobDetail.relatedPages.intro', defaultMessage: 'The role, city, employer and pay pages behind this listing.', description: 'One-line intro under the Explore more heading on the job page' })}
          links={relatedPages}
        />
      </div>
    </main>

    {/* Crossfade mini-header, mobile only, matching the native app's job screen: logo,
        company name, and Apply, sliding + fading in once the rail's own identity block
        scrolls out of view (translate rather than a plain opacity fade, so the eye actually
        catches the entrance instead of the bar just materializing in place). Sits below the
        main site nav (siteNavHeight), not on top of it. Desktop doesn't need this, the whole
        rail stays sticky in place instead (BrandRail.tsx). */}
    <div
      className={`lg:hidden fixed inset-x-0 z-20 flex items-center gap-2.5 px-4 shadow-[0_3px_10px_rgba(0,0,0,0.18)] transition-[transform,opacity] duration-300 [transition-timing-function:cubic-bezier(0.34,1.56,0.64,1)] ${
        miniHeaderVisible ? 'translate-y-0 opacity-100' : '-translate-y-3 opacity-0 pointer-events-none'
      }`}
      style={{ background: adjustedBrand.panel, color: adjustedBrand.on, top: siteNavHeight, height: 64 }}
      aria-hidden={!miniHeaderVisible}
    >
      <div className="h-8 w-8 flex-none rounded-lg bg-white p-1 box-border overflow-hidden flex items-center justify-center">
        <CompanyLogo
          companyName={displayJob.companyId || displayJob.companyName || ''}
          initialsSourceName={displayJob.companyName ?? displayJob.companyId}
          registryCompanyFormDescription={displayJob.registryCompanyFormDescription}
          isTyomarkkinatori={displayJob.isTyomarkkinatori}
          hostedLogoUrl={resolveSearchHitHostedLogoUrl(displayJob as unknown as Record<string, unknown>)}
          variant="bare"
          size={32}
          loading="eager"
        />
      </div>
      <div className="flex-1 min-w-0 [font-family:'Space_Grotesk',sans-serif] text-[14px] font-bold truncate">
        {fixMojibake(companyDisplayName)}
      </div>
      <div className="flex-none">
        <ApplyButton
          applyUrl={applyUrl}
          isApplied={isApplied}
          displayJob={displayJob}
          applyBtnStyle={{ backgroundColor: '#FFFFFF', color: adjustedBrand.accentOnWhite }}
          applyBtnHoverStyle={{ filter: 'brightness(0.96)' }}
          effectiveBrandColour={adjustedBrand.panel}
          onSetApplied={setAppliedJobState}
          isSecondary
          compact
        />
      </div>
    </div>
    </>
  );
}
